import { LicenseInfo } from "@mui/x-license";

import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import ErrorDisplay from "./components/UI/ErrorDisplay";
import Sidebar from "./components/UI/Sidebar";
import TopNavbar from "./components/UI/TopNavBar";
import Login from "./components/Utils/Login";
import PrivateRoute from "./components/Utils/PrivateRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ErrorProvider } from "./contexts/ErrorContext";
import "./index.css";
import CustomerDetailPage from "./pages/Customers/CustomerDetailPage";
import CustomersPage from "./pages/Customers/CustomerSummaryPage";
import DeliveriesDetailPage from "./pages/Deliveries/DeliveriesDetailPage";
import DeliveriesSummaryPage from "./pages/Deliveries/DeliveriesSummaryPage";
import SampleDetailPage from "./pages/Samples/SampleDetailPage";
import SamplesPage from "./pages/Samples/SamplesSummaryPage";
import DataOpsBrandsPage from "./pages/DataOps/DataOpsBrandsPage";
import DataOpsOmniDetailPage from "./pages/DataOps/DataOpsOmniDetailPage";
import StoreLocatorsPage from "./pages/DataOps/DataOpsOmnisPage";
import DataOpsPage from "./pages/DataOps/DataOpsPage";
import Home from "./pages/Home";
import AnalysisReportsMain from "./pages/Compliance/AnalysisReportsPage";
import ExportJobsMain from "./pages/Compliance/ExportJobsPage";

import BrandsInsightsPage from "./pages/Insights/BrandsInsightsPage";
import ExecutiveInsightsPage from "./pages/Insights/ExecutiveInsightsPage";
import SamplesInsightsPage from "./pages/Insights/SamplesInsightsPage";
import ShippingInsightsPage from "./pages/Insights/ShippingInsightsPage";
import ScheduleByDatePage from "./pages/Shipping/ScheduleByDatePage";
import ScheduleSummaryPage from "./pages/Shipping/ScheduleSummaryPage";
import ShippingWeeklyPage from "./pages/Shipping/ShippingWeeklyPage";
import TestPage from "./pages/TestPage";

import { format } from "date-fns";
import "./index.css";
import TaggingSuggestionsPage from "./pages/DataOps/Brands/TaggingSuggestionsPage";
import DataOpsBrandDetailPage from "./pages/DataOps/DataOpsBrandDetailPage";
import DataOpsTasksPage from "./pages/DataOps/DataOpsTasksPage";
import { ScheduleDeliveriesPage } from "./pages/Shipping/ScheduleDeliveriesPage";
import { ScheduleDeliveryPage } from "./pages/Shipping/ScheduleDeliveryPage";
import { useLocalStorage } from "./hooks/useLocalStorage";
import SchemaComplianceSummary from "components/Compliance/Schema/SchemaComplianceSummary";
import SchemaCustomerSummary from "components/Compliance/Schema/SchemaCustomerSummary";

const MUI_LICENSE: string = process.env.REACT_APP_MUIX_LICENSE_KEY ?? "";
LicenseInfo.setLicenseKey(MUI_LICENSE);

const AppContent: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Ganesh";
  }, [location]);

  const { loading, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useLocalStorage("sidebarOpen", true);

  // Override sidebar state when on home page
  useEffect(() => {
    if (location.pathname === "/") {
      setSidebarOpen(true);
    }
  }, [location.pathname, setSidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return <div></div>;
  }

  return (
    <>
      {isAuthenticated && (
        <>
          <TopNavbar
            isSidebarOpen={sidebarOpen}
            onHamburgerClick={toggleSidebar}
          />
          <Sidebar isOpen={sidebarOpen} onClose={toggleSidebar} />
        </>
      )}
      <main
        id="main-container"
        className={`${isAuthenticated ? "topNavOpen" : ""} ${
          isAuthenticated && sidebarOpen ? "sidebarOpen" : ""
        }`}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/customers/*"
            element={<PrivateRoute element={<CustomersPage />} />}
          />
          <Route
            path="/customer/:customer_id/*"
            element={<PrivateRoute element={<CustomerDetailPage />} />}
          />
          <Route
            path="/deliveries/*"
            element={<PrivateRoute element={<DeliveriesSummaryPage />} />}
          />
          <Route
            path="/delivery/:deliveryId/*"
            element={<PrivateRoute element={<DeliveriesDetailPage />} />}
          />
          <Route
            path="/samples"
            element={<PrivateRoute element={<SamplesPage />} />}
          />
          <Route
            path="/sample/:sampleId/*"
            element={<PrivateRoute element={<SampleDetailPage />} />}
          />

          <Route
            path="/shipping/weekly/:week_id?"
            element={<PrivateRoute element={<ShippingWeeklyPage />} />}
          />
          <Route
            path="/schedule/summary/:date_id"
            element={<PrivateRoute element={<ScheduleSummaryPage />} />}
          />
          <Route
            path="/schedule/summary"
            element={
              <PrivateRoute
                element={
                  <Navigate
                    to={`/schedule/summary/${format(new Date(), "yyyy-MM")}`}
                    replace
                  />
                }
              />
            }
          />
          <Route
            path="/schedule/deliveries/:delivery_id"
            element={<PrivateRoute element={<ScheduleDeliveryPage />} />}
          />
          <Route
            path="/schedule/deliveries"
            element={<PrivateRoute element={<ScheduleDeliveriesPage />} />}
          />
          <Route
            path="/schedule/date/:date_id"
            element={<PrivateRoute element={<ScheduleByDatePage />} />}
          />
          <Route
            path="/data-ops"
            element={<PrivateRoute element={<DataOpsPage />} />}
          />
          <Route
            path="/data-ops/analysis-reports/*"
            element={<PrivateRoute element={<AnalysisReportsMain />} />}
          />
          <Route
            path="/data-ops/export-jobs/*"
            element={<PrivateRoute element={<ExportJobsMain />} />}
          />
          <Route
            path="/customer-schemas"
            element={<PrivateRoute element={<SchemaComplianceSummary />} />}
          />
          <Route
            path="/customer-schemas/:customer_id"
            element={<PrivateRoute element={<SchemaCustomerSummary />} />}
          />
          <Route
            path="/data-ops/brands/tagging-suggestions/"
            element={<PrivateRoute element={<TaggingSuggestionsPage />} />}
          />
          <Route
            path="/data-ops/brands/*"
            element={<PrivateRoute element={<DataOpsBrandsPage />} />}
          />
          <Route
            path="/data-ops/brand/:brand_id/*"
            element={<PrivateRoute element={<DataOpsBrandDetailPage />} />}
          />
          <Route
            path="/data-ops/omnis/*"
            element={<PrivateRoute element={<StoreLocatorsPage />} />}
          />
          <Route
            path="/data-ops/omni/:omni_key"
            element={<PrivateRoute element={<DataOpsOmniDetailPage />} />}
          />
          <Route
            path="/data-ops/tasks/*"
            element={<PrivateRoute element={<DataOpsTasksPage />} />}
          />
          <Route
            path="/testing"
            element={<PrivateRoute element={<TestPage />} />}
          />
          <Route
            path="/insights"
            element={<PrivateRoute element={<ExecutiveInsightsPage />} />}
          />
          <Route
            path="/insights/shipping"
            element={<PrivateRoute element={<ShippingInsightsPage />} />}
          />
          <Route
            path="/insights/samples"
            element={<PrivateRoute element={<SamplesInsightsPage />} />}
          />
          <Route
            path="/insights/brands"
            element={<PrivateRoute element={<BrandsInsightsPage />} />}
          />
        </Routes>
      </main>
    </>
  );
};

const App: React.FC = () => (
  <Router>
    <ErrorProvider>
      <ErrorDisplay />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorProvider>
  </Router>
);

export default App;
